/* eslint-disable react/jsx-no-target-blank */
import * as React from "react";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable, isSortable } from "@dnd-kit/react/sortable";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardMedia from "@mui/material/CardMedia";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const DEFAULT_CARD_WIDTH = 180;
const DEFAULT_ASPECT_RATIO = "9 / 16";

function SortableBannerCard({
  banner,
  index,
  editBanner,
  deleteBanner,
  toggleBannerActive,
  editingId,
  togglingId,
  cardWidth = DEFAULT_CARD_WIDTH,
  aspectRatio = DEFAULT_ASPECT_RATIO,
}) {
  const { ref, handleRef, isDragging } = useSortable({
    id: banner.id,
    index,
  });

  const isEditing = editingId === banner.id;
  const isActive = Boolean(banner.isActive);
  const isToggling = togglingId === banner.id;

  return (
    <Card
      ref={ref}
      sx={{
        width: cardWidth,
        flexShrink: 0,
        opacity: isDragging ? 0.65 : isActive ? 1 : 0.55,
        border: isEditing ? 2 : 1,
        borderColor: isEditing ? "primary.main" : "divider",
        boxShadow: isDragging ? 6 : 1,
        filter: isActive ? "none" : "grayscale(40%)",
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Tooltip title="Drag to reorder">
          <IconButton
            ref={handleRef}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 1,
              bgcolor: "rgba(255,255,255,0.92)",
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Chip
          label={banner.order ?? "—"}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            bgcolor: "rgba(255,255,255,0.92)",
          }}
        />
        {!isActive ? (
          <Chip
            label="Inactive"
            size="small"
            color="warning"
            sx={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
            }}
          />
        ) : null}
        <CardMedia
          component="a"
          href={banner?.imageUrl || undefined}
          target="_blank"
          rel="noreferrer"
          image={banner?.imageUrl}
          alt={`Banner ${banner.id}`}
          sx={{
            width: "100%",
            aspectRatio,
            objectFit: "cover",
            bgcolor: "grey.100",
            display: "block",
            cursor: banner?.imageUrl ? "pointer" : "default",
            textDecoration: "none",
            "&:hover": banner?.imageUrl
              ? { opacity: 0.92 }
              : undefined,
          }}
        />
      </Box>
      <Box sx={{ px: 1.5, py: 1 }}>
        {banner?.link ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {banner.link}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled">
            No click link
          </Typography>
        )}
      </Box>
      <CardActions
        sx={{
          px: 1,
          pb: 1,
          pt: 0,
          flexDirection: "column",
          alignItems: "stretch",
          gap: 0.5,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={isActive}
              disabled={isToggling}
              onChange={() => toggleBannerActive?.(banner)}
              color="success"
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {isActive ? "Active" : "Inactive"}
            </Typography>
          }
          sx={{ mx: 0, justifyContent: "space-between" }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tooltip title="Edit">
            <IconButton
              onClick={() => editBanner(banner)}
              size="small"
              color={isEditing ? "primary" : "default"}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box>
            <Tooltip title={banner?.link ? banner?.link : "No link"}>
              <span>
                <IconButton
                  component="a"
                  href={banner?.link || undefined}
                  target="_blank"
                  rel="noreferrer"
                  size="small"
                  disabled={!banner?.link}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => deleteBanner(banner?.id)}
                size="small"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardActions>
    </Card>
  );
}

export function sortBanners(banners = []) {
  return [...banners].sort((a, b) => {
    const aHasOrder = a.order != null;
    const bHasOrder = b.order != null;

    if (aHasOrder && bHasOrder && a.order !== b.order) {
      return a.order - b.order;
    }
    if (aHasOrder !== bHasOrder) {
      return aHasOrder ? -1 : 1;
    }
    return a.id - b.id;
  });
}

export default function MediaCard(props) {
  const {
    banners,
    onReorder,
    editBanner,
    deleteBanner,
    toggleBannerActive,
    editingId,
    togglingId,
    cardWidth = DEFAULT_CARD_WIDTH,
    aspectRatio = DEFAULT_ASPECT_RATIO,
  } = props;
  const [items, setItems] = React.useState(banners);
  const isDraggingRef = React.useRef(false);
  const snapshotRef = React.useRef(banners);

  React.useEffect(() => {
    if (!isDraggingRef.current) {
      setItems(sortBanners(banners));
    }
  }, [banners]);

  const handleDragEnd = (event) => {
    isDraggingRef.current = false;

    if (event.canceled) {
      setItems(snapshotRef.current);
      return;
    }

    const { source } = event.operation;

    if (!isSortable(source)) {
      return;
    }

    const { initialIndex, index } = source;

    if (initialIndex === index) {
      return;
    }

    setItems((currentItems) => {
      const nextItems = [...currentItems];
      const [movedItem] = nextItems.splice(initialIndex, 1);
      nextItems.splice(index, 0, movedItem);

      const reordered = nextItems.map((banner, itemIndex) => ({
        ...banner,
        order: itemIndex + 1,
      }));

      onReorder?.(reordered);
      return reordered;
    });
  };

  if (!items.length) {
    return (
      <Box
        sx={{
          py: 6,
          px: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          textAlign: "center",
        }}
      >
        <Typography color="text.secondary">
          No banners yet. Add one using the form above.
        </Typography>
      </Box>
    );
  }

  return (
    <DragDropProvider
      onDragStart={() => {
        isDraggingRef.current = true;
        snapshotRef.current = items;
      }}
      onDragEnd={handleDragEnd}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 1,
          px: 0.5,
        }}
      >
        {items.map((banner, index) => (
          <SortableBannerCard
            key={banner.id}
            banner={banner}
            index={index}
            editBanner={editBanner}
            deleteBanner={deleteBanner}
            toggleBannerActive={toggleBannerActive}
            editingId={editingId}
            togglingId={togglingId}
            cardWidth={cardWidth}
            aspectRatio={aspectRatio}
          />
        ))}
      </Box>
    </DragDropProvider>
  );
}
